import { supabase } from '@/integrations/supabase/client';

export class InstantEmailFix {
  private static readonly PLATFORM_URL = 'https://floridahighwaymenauction.com';

  /**
   * Send email with retry logic
   */
  private static async sendEmail(to: string, subject: string, htmlContent: string, emailType: string): Promise<boolean> {
    try {
      console.log(`📧 Sending ${emailType} email to ${to}`);
      
      const { data: result, error } = await supabase.functions.invoke('send-email-notification', {
        body: {
          to,
          subject,
          html_content: htmlContent,
          email_type: emailType
        }
      });

      if (error) {
        console.error(`❌ Email failed:`, error);
        return false;
      }

      console.log(`✅ Email sent successfully:`, result);
      return true;
    } catch (error) {
      console.error(`❌ Email error:`, error);
      return false;
    }
  }

  /**
   * Manual trigger for bid approval emails
   */
  static async triggerBidApprovalEmail(bidId: string): Promise<void> {
    try {
      console.log(`🔄 Manually triggering bid approval email for bid: ${bidId}`);

      // Get bid with user and lot info
      const { data: bid, error } = await supabase
        .from('bids')
        .select(`
          id, user_id, lot_id, amount, status,
          profiles!inner(email, first_name, last_name),
          lots!inner(id, lot_number, artwork!inner(title, artist!inner(name)))
        `)
        .eq('id', bidId as any)
        .eq('status', 'approved' as any)
        .single();

      if (error || !bid) {
        console.error('❌ Could not fetch bid:', error);
        return;
      }

      const userProfile = (bid as any).profiles;
      const lot = (bid as any).lots;
      const artwork = lot?.artwork;
      const artist = artwork?.artist;

      if (!userProfile?.email) {
        console.error('❌ No email found for user');
        return;
      }

      const userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || 'Valued Customer';

      const template = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>🎉 Bid Approved - You're the High Bidder!</title>
        </head>
        <body style="font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f5f5f5;">
          <div style="max-width: 600px; margin: 0 auto; background: white; padding: 30px; border-radius: 8px;">
            <h1 style="color: #8B4513; text-align: center;">🎉 Bid Approved!</h1>
            <h2 style="color: #2d2d2d; text-align: center;">You're the High Bidder</h2>
            
            <p>Dear ${userName},</p>
            
            <p>Congratulations! Your bid has been approved and you are currently the high bidder on:</p>
            
            <div style="border: 2px solid #8B4513; padding: 20px; margin: 20px 0; border-radius: 8px;">
              <h3 style="margin: 0 0 10px 0; color: #2d2d2d;">${artwork?.title || 'Unknown Artwork'}</h3>
              <p style="margin: 0 0 10px 0; color: #8B4513; font-style: italic;">by ${artist?.name || 'Unknown Artist'}</p>
              <p style="margin: 0;"><strong>Lot #${lot?.lot_number || 'Unknown'}</strong></p>
              <p style="margin: 10px 0 0 0; font-size: 18px; color: #8B4513;"><strong>Your Winning Bid: $${(bid as any).amount?.toLocaleString() || '0'}</strong></p>
            </div>
            
            <p>Important reminders:</p>
            <ul>
              <li>You are currently the high bidder on this lot</li>
              <li>We'll notify you immediately if another bidder outbids you</li>
              <li>Monitor the auction closely until it ends</li>
              <li>If you win, we'll send payment and collection instructions</li>
            </ul>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${this.PLATFORM_URL}/lots/${lot?.id}" 
                 style="background-color: #8B4513; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
                Monitor This Lot
              </a>
            </div>
            
            <p style="text-align: center; color: #666; font-size: 14px;">
              Florida Highwaymen Auctions LLC<br>
              <a href="mailto:info@floridahighwaymenauction.com">info@floridahighwaymenauction.com</a>
            </p>
          </div>
        </body>
        </html>
      `;

      await this.sendEmail(
        userProfile.email,
        `🎉 Bid Approved - You're the High Bidder on ${artwork?.title || 'Florida Highwaymen Artwork'}!`,
        template,
        'bid_approved'
      );

    } catch (error) {
      console.error('❌ Error triggering bid approval email:', error);
    }
  }

  /**
   * Check for recent approved bids and send missing emails
   */
  static async checkAndSendMissedEmails(): Promise<void> {
    try {
      console.log('🔍 Checking for recently approved bids needing emails...');

      // Get bids approved in the last 5 minutes
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      
      // Use approved_at if available; fall back to updated_at
      let { data: recentBids, error } = await supabase
        .from('bids')
        .select('id, status, approved_at')
        .eq('status', 'approved' as any)
        .gte('approved_at', fiveMinutesAgo)
        .order('approved_at', { ascending: false });

      if (error) {
        console.warn('instantEmailFix: approved_at query failed, falling back to updated_at', error);
        const fallback = await supabase
          .from('bids')
          .select('id, status, updated_at')
          .eq('status', 'approved' as any)
          .gte('updated_at', fiveMinutesAgo)
          .order('updated_at', { ascending: false });
        recentBids = fallback.data as any;
      }

      console.log(`📋 Found ${recentBids?.length || 0} recently approved bids`);

      for (const bid of recentBids || []) {
        await this.triggerBidApprovalEmail((bid as any).id);
        // Wait a bit between emails to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log('✅ Finished checking missed emails');
    } catch (error) {
      console.error('❌ Error checking missed emails:', error);
    }
  }

  /**
   * Initialize the missed email checker
   */
  static initialize(): void {
    console.log('🚀 InstantEmailFix initialized - starting missed email checker');
    
    // Run immediately
    this.checkAndSendMissedEmails();
    
    // Then run every 2 minutes
    setInterval(() => {
      this.checkAndSendMissedEmails();
    }, 2 * 60 * 1000);
  }
}

// Auto-initialize when the service is imported
InstantEmailFix.initialize(); 